import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { periodApi } from "../../../api/periods";
import { tsApi } from "../../../api/timesheets";
import { PageHeader } from "../../../components/ComingSoon";
import { StatusPill } from "../../../components/ui";
import { LoadState, useLoad } from "../shared";
import { AdjustmentsPanel } from "./AdjustmentsPanel";
import { PayrollReview } from "./PayrollReview";
import { PeriodActions } from "./PeriodActions";
import { SnapshotHistory } from "./SnapshotHistory";

export default function PeriodDetail() {
  const { periodId = "" } = useParams();
  const [nonce, setNonce] = useState(0);
  const loader = useCallback(() => Promise.all([periodApi.detail(periodId), tsApi.blocking(periodId)]), [periodId, nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad(loader);
  const refresh = () => setNonce((n) => n + 1);
  const [d, blocking] = data ?? [null, null];
  const locked = d?.period.status === "FINALIZED";
  return (
    <div data-testid="period-detail-page" className="animate-rise space-y-8">
      <Link to="/admin/pay-periods" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid="period-back-link"><ArrowLeft className="h-3 w-3" />Pay Periods</Link>
      <PageHeader title={d ? `${d.period.start_date} → ${d.period.end_date}` : "Pay period"} />
      <LoadState error={error} loading={!data && !error} testId="period-detail-load" />
      {d && blocking && (
        <>
          <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100" data-testid="period-gross-banner">{d.banner}</div>
          <div className="flex flex-wrap items-center gap-4">
            <StatusPill status={d.period.status} testId="period-status" />
            {locked && <span className="inline-flex items-center gap-1 text-xs text-zinc-300" data-testid="period-locked-note"><Lock className="h-3 w-3" />Locked · FINAL v{d.period.final_version}{d.period.demo_finalization ? " · DEMO TEST POLICY – NOT FOR PAYROLL" : ""}</span>}
            <span className="text-xs text-zinc-500">{d.policy_banner.text}</span>
            <div className="ml-auto"><PeriodActions periodId={periodId} status={d.period.status} blocking={blocking.count} hasApprovedRun={d.has_approved_run} onDone={refresh} /></div>
          </div>
          <section className="rounded-sm border border-white/10 p-5" data-testid="period-blocking-list">
            <p className="label-caps mb-2 text-zinc-400">Blocking items · {blocking.count}</p>
            {!blocking.items.length ? <p className="text-sm text-emerald-300" data-testid="period-blocking-clear">Nothing blocking — this period can be finalized.</p> : (
              <ul className="space-y-1 text-xs text-zinc-300">
                {blocking.items.map((i, n) => <li key={n} data-testid={`period-blocking-item-${n}`}><span className="font-mono font-semibold text-red-200">{i.code}</span>{i.employee_name ? ` · ${i.employee_name}` : ""} — {i.message}</li>)}
              </ul>
            )}
          </section>
          <PayrollReview periodId={periodId} finalized={locked} />
          <AdjustmentsPanel periodId={periodId} items={d.adjustments} locked={locked} onDone={refresh} />
          <SnapshotHistory data={d} />
        </>
      )}
    </div>
  );
}
